import React from 'react'

import moment from 'moment'
import styled from 'styled-components'

import { Box, Flex } from 'app/components/design-system-components'
import { Text } from 'app/components/design-system-components/typography'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import * as helpers from 'app/sm/helpers'
import { formatRent } from 'app/sm/helpers'
import { getPropertyAvailabilityDate } from 'app/utils/datetime/helpers'

const PropertyItemContainer = styled(Box)`
  @media print {
    page-break-inside: avoid;
    margin-bottom: ${theme.space[3]}px;
  }
`

const StyledPropertyImage = styled.img`
  width: ${theme.width[18]}px;
  height: ${theme.height[8]}px;
  background-size: auto;
  object-fit: cover;
`

const FullBoxWidthText = styled(Box)`
  text-align: justify;
  /* a height for one line */
  height: 1rem;

  :after {
    /* used to stretch word */
    content: '';
    width: 100%;
    display: inline-block;
  }

  @media print {
    height: 0.8rem;
    line-height: 1.1;
  }
`

const CharListContainer = styled(Box)`
  font-size: ${theme.fontSizes[3]}px;
  line-height: 1.1;
`

const MetaDataContainer = styled(Flex)`
  font-size: ${theme.fontSizes[2]}px;
  margin-right: ${theme.space[0]}px;
  margin-bottom: ${theme.space[1]}px;
`

const LargeBoldText = styled(Text)`
  font-size: ${theme.fontSizes[3]}px;
  font-weight: ${theme.fontWeights.bold};
  color: ${(props) => props.color || theme.colors.gray800};
`

const LargeGrayText = styled(Text)`
  font-size: ${theme.fontSizes[3]}px;
  color: ${theme.colors.gray600};
`

const LargeSemiboldText = styled(Text)`
  font-size: ${theme.fontSizes[2]}px;
  font-weight: ${theme.fontWeights.semibold};
`

const DescriptionText = styled(Text)`
  font-size: ${theme.fontSizes[3]}px;
  line-height: 1.4;
  margin-bottom: ${theme.space[2]}px;
`

const FeatureIconText = styled(Text)`
  font-size: ${theme.fontSizes[3]}px;
`

const PropItem = ({ data = {} }) => {
  const {
    address = {},
    bedrooms = '',
    bathrooms = 0,
    garages = 0,
    carports = 0,
    parking = 0,
    description = '',
    livingAreaDesc = '',
    nextViewings = [],
    activeOffer,
  } = data

  const { weeklyRentDisplay, availableFrom = '' } = activeOffer || {}
  const { friendlyName = '', suburb = '' } = address
  const formattedAddress = `${friendlyName}, ${suburb} `
  const image = helpers.getMainImage(data)
  const formatAvailFrom = getPropertyAvailabilityDate(availableFrom, null)

  const propertyFeatures = [
    { icon: helpers.iconCollection.bedroom, value: bedrooms },
    { icon: helpers.iconCollection.bathrooms, value: bathrooms },
    {
      icon: helpers.iconCollection.cars,
      value: garages || carports || parking,
    },
  ]
  const propertyDescription = description || livingAreaDesc
  const trimmedDescription = helpers.trimFileLength(180)(propertyDescription)
  const descriptionText = helpers.stripHTMLTags(trimmedDescription)

  const renderNextViewings = () => (
    <>
      {!nextViewings.length && <LargeSemiboldText>TBA</LargeSemiboldText>}
      {nextViewings.map(({ startDate, duration }, index) => (
        <FullBoxWidthText key={index}>
          <LargeSemiboldText>
            {moment(startDate).local().format(helpers.viewingNoYearTimeFormat)}{' '}
            {`(${duration}m)`}
          </LargeSemiboldText>
        </FullBoxWidthText>
      ))}
    </>
  )

  const renderPropertyFeatures = () => (
    <CharListContainer as="ul" className="char-list">
      {propertyFeatures.map((feature, index) => (
        <Box as="li" key={index}>
          <FeatureIconText as="span" className={feature.icon} />
          {feature.value}
        </Box>
      ))}
    </CharListContainer>
  )

  return (
    <PropertyItemContainer as="article" className="prop-item d-flex">
      <Box className="img-wrap">
        <StyledPropertyImage
          src={image}
          alt={formattedAddress || 'Property image'}
        />
      </Box>
      <Box className="descr-wrap">
        <Flex justifyContent="space-between">
          <Box className="item-heading">
            <Box>
              <LargeBoldText as="span">
                {formatRent(weeklyRentDisplay)}
              </LargeBoldText>
              <LargeGrayText as="span"> Avail:</LargeGrayText>
              <LargeBoldText as="span"> {formatAvailFrom}</LargeBoldText>
            </Box>
            <Box>
              <LargeSemiboldText as="strong">
                {formattedAddress}
              </LargeSemiboldText>
            </Box>
          </Box>
          <Box>{renderPropertyFeatures()}</Box>
        </Flex>

        <DescriptionText>{descriptionText}</DescriptionText>

        <Flex>
          <MetaDataContainer className="meta-data">
            <Box mr={`${theme.space[1]}px`}>
              <LargeSemiboldText>Next viewings:</LargeSemiboldText>
            </Box>
            <Box mr={`${theme.space[2]}px`}>{renderNextViewings()}</Box>
          </MetaDataContainer>
        </Flex>
      </Box>
    </PropertyItemContainer>
  )
}

export default PropItem
